import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, Calendar, Clock, User } from "lucide-react";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { getArticle, JOURNAL } from "@/data/journal";
import { getCondition } from "@/data/care";
import { medicalWebPageJsonLd, breadcrumbListJsonLd } from "@/data/schema";
import { api, WHATSAPP_LINK } from "@/lib/api";

export default function JournalArticle() {
  const { slug } = useParams();
  const staticArticle = getArticle(slug);
  const [a, setA] = useState(staticArticle || null);
  const [loading, setLoading] = useState(!staticArticle);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/journal/${slug}`);
        if (!cancel) setA(data);
      } catch {
        // fall through — static fallback used if present, else 404
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    // fire view once per session per slug
    try {
      const key = `journal_view:${slug}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        api.post(`/journal/${slug}/event/view`).catch(() => {});
      }
    } catch {}
    return () => { cancel = true; };
  }, [slug]);

  if (loading) return <div className="pt-40 text-center kbd">Loading…</div>;
  if (!a) return <Navigate to="/journal" replace />;
  const cond = getCondition(a.condition);
  const others = JOURNAL.filter((x) => x.slug !== a.slug).slice(0, 3);

  return (
    <div data-testid={`journal-article-${a.slug}`} className="pt-28 md:pt-36 pb-28">
      <SEO
        title={`${a.title} — Case study · Dr Tegbir Vascular`}
        description={a.deck}
        keywords={a.tags}
      />
      <JsonLd data={[
        medicalWebPageJsonLd({ name: a.title, description: a.deck, url: `/journal/${a.slug}`, about: cond?.name }),
        breadcrumbListJsonLd([{ to: "/", label: "Home" }, { to: "/journal", label: "Journal" }, { label: a.title }]),
        {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": a.title,
          "description": a.deck,
          "datePublished": a.date,
          "author": { "@type": "Physician", "name": a.author },
          "image": a.hero,
        },
      ]}/>

      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <FadeUp>
          <Breadcrumbs trail={[{ to: "/", label: "Home" }, { to: "/journal", label: "Journal" }, { label: a.title }]} />
        </FadeUp>

        {/* Hero */}
        <div className="mt-12">
          <FadeUp>
            <div className="flex flex-wrap items-center gap-3 kbd text-ink-muted">
              <span className="inline-flex items-center gap-1"><Calendar size={11}/> {a.date}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><Clock size={11}/> {a.readTime}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><User size={11}/> {a.author}</span>
            </div>
          </FadeUp>
          <h1 className="mt-6 font-serif text-5xl md:text-[6vw] leading-[0.98] tracking-tightest max-w-4xl">
            <MaskedReveal text={a.title} stagger={0.04}/>
          </h1>
          <FadeUp delay={0.25}>
            <p className="mt-8 text-xl md:text-2xl font-serif italic max-w-3xl leading-snug">"{a.deck}"</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {(a.tags || []).map((t) => <span key={t} className="kbd border border-ink/15 px-2 py-1 rounded-full">{t}</span>)}
            </div>
          </FadeUp>
        </div>

        <FadeUp className="mt-16" delay={0.3}>
          <div className="aspect-[16/8] overflow-hidden hairline relative">
            <img src={a.hero} alt="" className="w-full h-full object-cover" loading="lazy"/>
            <div className="noise-overlay absolute inset-0"/>
          </div>
        </FadeUp>

        {/* Chapters */}
        <div className="mt-24 space-y-24">
          {a.chapters.map((ch, i) => (
            <FadeUp key={ch.n} delay={i * 0.04}>
              <div className="grid grid-cols-12 gap-6 md:gap-12">
                <div className="col-span-12 md:col-span-3">
                  <div className="kbd">— Chapter</div>
                  <div className="font-serif text-7xl md:text-8xl leading-none tracking-tightest text-crimson mt-2">{ch.n}</div>
                  <div className="mt-4 font-serif text-2xl tracking-tightest">{ch.title}</div>
                </div>
                <div className="col-span-12 md:col-span-9">
                  <p className="text-lg md:text-xl leading-[1.7] text-ink max-w-3xl">{ch.body}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>

        {/* Lesson */}
        <FadeUp className="mt-24">
          <div className="hairline-t pt-12">
            <div className="kbd">— The lesson</div>
            <blockquote className="mt-6 font-serif italic text-3xl md:text-5xl leading-[1.2] tracking-tightest max-w-4xl">
              "{a.lesson}"
            </blockquote>
          </div>
        </FadeUp>

        {/* Disclaimer */}
        <FadeUp className="mt-16">
          <div className="hairline p-5 md:p-6 bg-white/60 kbd text-ink-muted">
            Anonymised with consent. Details altered where they did not affect the clinical point. Individual outcomes vary. Nothing here replaces personal medical advice.
          </div>
        </FadeUp>

        {/* Related */}
        <div className="mt-24">
          <FadeUp>
            <div className="hairline-t pt-8 flex items-center justify-between">
              <div className="kbd">— More cases</div>
              <Link to="/journal" className="kbd hover:text-ink">All cases →</Link>
            </div>
          </FadeUp>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {others.map((o) => (
              <Link key={o.slug} to={`/journal/${o.slug}`} className="group hairline bg-white/60 hover:bg-white transition-colors p-5">
                <div className="font-serif text-xl tracking-tightest group-hover:text-crimson">{o.title}</div>
                <p className="mt-2 text-sm text-ink-muted">{o.deck}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* CTA */}
        <FadeUp className="mt-24">
          <div className="hairline-t pt-12 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-8">
              <h3 className="font-serif text-4xl md:text-5xl tracking-tightest leading-tight max-w-3xl">
                Is your case similar?
              </h3>
              <p className="mt-4 text-ink-muted max-w-xl">Send us your imaging for a written second opinion — you'll hear back within 2 working days.</p>
            </div>
            <div className="col-span-12 md:col-span-4 md:pl-8 self-end flex flex-wrap gap-3">
              <Link to={cond ? `/second-opinion?condition=${cond.slug}` : "/second-opinion"} onClick={() => api.post(`/journal/${a.slug}/event/so_click`).catch(() => {})} data-testid="journal-so-cta" className="btn-pill-inv">Second opinion <ArrowUpRight size={14}/></Link>
              <a target="_blank" rel="noreferrer" href={WHATSAPP_LINK(`Hello, I read the case '${a.title}' on your website.`)} className="btn-pill">WhatsApp</a>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
